'use client';

import React, { useState } from 'react';
import Image from 'next/image';

interface AppImageProps {
    src: string;
    alt: string;
    width?: number;
    height?: number;
    className?: string;
    priority?: boolean;
    quality?: number;
    placeholder?: 'blur' | 'empty';
    blurDataURL?: string;
    fill?: boolean;
    sizes?: string;
    onClick?: () => void;
    fallbackSrc?: string;
    loading?: 'lazy' | 'eager';
    [key: string]: any;
}

function AppImage({
    src,
    alt,
    width,
    height,
    className = '',
    priority = false,
    quality = 85,
    placeholder = 'empty',
    blurDataURL,
    fill = false,
    sizes,
    onClick,
    fallbackSrc = '/assets/images/no_image.png',
    loading = 'lazy',
    ...props
}: AppImageProps) {
    const [imageSrc, setImageSrc] = useState(src);
    const [hasError, setHasError] = useState(false);

    const isExternal = imageSrc.startsWith('http://') || imageSrc.startsWith('https://');
    const isLocal = imageSrc.startsWith('/') || imageSrc.startsWith('./') || imageSrc.startsWith('data:');

    const handleError = () => {
        if (!hasError && imageSrc !== fallbackSrc) {
            setImageSrc(fallbackSrc);
            setHasError(true);
        }
    };

    const commonClassName = `${className} ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`;

    // Use Next.js Image for local images for better optimization
    if (isLocal && !isExternal) {
        if (fill) {
            return (
                <div className={`relative ${className}`} style={{ width: width || '100%', height: height || '100%' }}>
                    <Image
                        src={imageSrc}
                        alt={alt}
                        fill
                        className={commonClassName}
                        quality={quality}
                        priority={priority}
                        sizes={sizes || '100vw'}
                        onError={handleError}
                        onClick={onClick}
                        placeholder={placeholder}
                        blurDataURL={blurDataURL}
                        {...props}
                    />
                </div>
            );
        }

        return (
            <Image
                src={imageSrc}
                alt={alt}
                width={width || 800}
                height={height || 600}
                className={commonClassName}
                quality={quality}
                priority={priority}
                sizes={sizes}
                onError={handleError}
                onClick={onClick}
                placeholder={placeholder}
                blurDataURL={blurDataURL}
                loading={priority ? undefined : 'lazy'}
                {...props}
            />
        );
    }

    // For external URLs, use optimized img tag with explicit dimensions
    const imgStyle: React.CSSProperties = {
        width: width ? `${width}px` : '100%',
        height: height ? `${height}px` : 'auto',
    };

    if (fill) {
        return (
            <div className={`relative ${className}`} style={{ width: width || '100%', height: height || '100%' }}>
                <img
                    src={imageSrc}
                    alt={alt}
                    className={`${commonClassName} absolute inset-0 w-full h-full object-cover`}
                    onError={handleError}
                    onClick={onClick}
                    loading={priority ? 'eager' : loading}
                    decoding="async"
                    fetchPriority={priority ? 'high' : 'auto'}
                    width={width}
                    height={height}
                    {...props}
                />
            </div>
        );
    }

    return (
        <img
            src={imageSrc}
            alt={alt}
            className={commonClassName}
            onError={handleError}
            onClick={onClick}
            loading={priority ? 'eager' : loading}
            decoding="async"
            fetchPriority={priority ? 'high' : 'auto'}
            style={imgStyle}
            width={width}
            height={height}
            {...props}
        />
    );
}

export default AppImage;